import {Alert, Button, Group, Loader, Paper, Stack, Text, TextInput, Title} from '@mantine/core'
import {useForm} from '@mantine/form'
import {Link} from '@tanstack/react-router'
import {useEffect, useState} from 'react'

import {isAdmin} from 'src/auth/is-admin'
import {useCurrentUser} from 'src/auth/use-current-user'
import {DetailPageBackLink} from 'src/ui/detail-page-back-link'

import type {Worker} from '../workers/schemas'
import {useWorkers} from '../workers/use-workers'
import type {WorkerType} from './schemas'
import {useUpdateWorkerType, useWorkerType} from './use-worker-types'
import {WorkerTypeRoleSwitches} from './worker-type-form-modal'

export type WorkerTypeDetailsPageState =
    | 'loading'
    | 'error'
    | 'notFound'
    | 'reading'
    | 'editing'
    | 'saving'

type WorkerTypeFormValues = {
    name: string
    isDriver: boolean
    isTechnician: boolean
}

function buildWorkerTypeFormValues(workerType: WorkerType): WorkerTypeFormValues {
    return {
        name: workerType.name,
        isDriver: workerType.isDriver,
        isTechnician: workerType.isTechnician,
    }
}

function formatWorkerTypeRoleLabel(workerType: WorkerType): string {
    if (workerType.isDriver) {
        return 'Fills driver slots'
    }

    return workerType.isTechnician ? 'Field technician' : 'Other crew'
}

type WorkerTypeDetailsPageProps = {
    typeId: string
}

function validateWorkerTypeForm(values: WorkerTypeFormValues) {
    const errors: Partial<Record<keyof WorkerTypeFormValues, string>> = {}
    if (!values.name.trim()) {
        errors.name = 'Type name is required'
    }
    return errors
}

function isNotFoundError(error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to load worker type'
    return message.toLowerCase().includes('not found')
}

export function WorkerTypeDetailsPage({typeId}: WorkerTypeDetailsPageProps) {
    const user = useCurrentUser()
    const {
        data: workerType,
        isLoading: typeLoading,
        isError: typeError,
        error: typeErrorObj,
        refetch: refetchType,
    } = useWorkerType(typeId)
    const {
        data: workers = [],
        isLoading: workersLoading,
        isError: workersError,
        error: workersErrorObj,
        refetch: refetchWorkers,
    } = useWorkers({workerTypeId: typeId})
    const updateWorkerType = useUpdateWorkerType()
    const [pageState, setPageState] = useState<WorkerTypeDetailsPageState>('loading')
    const [errorMessage, setErrorMessage] = useState<string | null>(null)

    const form = useForm<WorkerTypeFormValues>({
        initialValues: {name: '', isDriver: false, isTechnician: false},
        validate: validateWorkerTypeForm,
    })

    useEffect(() => {
        if (typeLoading) {
            setPageState('loading')
            return
        }

        if (typeError) {
            if (isNotFoundError(typeErrorObj)) {
                setPageState('notFound')
                return
            }

            setErrorMessage(
                typeErrorObj instanceof Error ? typeErrorObj.message : 'Failed to load worker type',
            )
            setPageState('error')
            return
        }

        if (workerType) {
            form.setValues(buildWorkerTypeFormValues(workerType))
            if (pageState === 'loading' || pageState === 'error') {
                setPageState('reading')
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- form identity is stable; omit to avoid reload on edit
    }, [workerType, typeLoading, typeError, typeErrorObj])

    function handleEdit() {
        if (!workerType) {
            return
        }

        form.setValues(buildWorkerTypeFormValues(workerType))
        setPageState('editing')
    }

    function handleCancel() {
        if (!workerType) {
            return
        }

        form.setValues(buildWorkerTypeFormValues(workerType))
        setPageState('reading')
    }

    async function handleSave() {
        if (!workerType) {
            return
        }

        const validation = form.validate()
        if (validation.hasErrors) {
            return
        }

        setPageState('saving')
        setErrorMessage(null)

        try {
            await updateWorkerType.mutateAsync({
                id: workerType.id,
                input: {
                    name: form.values.name.trim(),
                    isDriver: form.values.isDriver,
                    isTechnician: form.values.isTechnician,
                },
            })
            setPageState('reading')
        } catch (saveError) {
            const message =
                saveError instanceof Error ? saveError.message : 'Failed to save worker type'
            setErrorMessage(message)
            setPageState('editing')
        }
    }

    const workersErrorMessage =
        workersError && workersErrorObj instanceof Error
            ? workersErrorObj.message
            : workersError
              ? 'Failed to load workers'
              : null

    if (pageState === 'loading') {
        return (
            <Group justify="center" py="xl">
                <Loader />
            </Group>
        )
    }

    if (pageState === 'notFound') {
        return (
            <Stack gap="md">
                <Title order={1}>Worker type not found</Title>
                <Text c="dimmed">This type may have been removed or the link is invalid.</Text>
                <DetailPageBackLink to="/workers/types" listLabel="worker types" />
            </Stack>
        )
    }

    if (pageState === 'error' || !workerType) {
        return (
            <Stack gap="md">
                <Title order={1}>Worker type details</Title>
                <Alert color="red" title="Could not load worker type">
                    {errorMessage ?? 'Something went wrong.'}
                </Alert>
                <Button onClick={() => void refetchType()} variant="light">
                    Retry
                </Button>
            </Stack>
        )
    }

    const isEditing = pageState === 'editing' || pageState === 'saving'

    return (
        <Stack gap="md">
            <Stack gap={4}>
                <DetailPageBackLink to="/workers/types" listLabel="worker types" />

                <Group justify="space-between" align="flex-start">
                    <Title order={1}>{workerType.name}</Title>

                    {pageState === 'reading' && isAdmin(user) && (
                        <Button onClick={handleEdit}>Edit</Button>
                    )}

                    {isEditing && (
                        <Group gap="sm">
                            <Button
                                variant="default"
                                onClick={handleCancel}
                                disabled={pageState === 'saving'}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={() => void handleSave()}
                                loading={pageState === 'saving'}
                            >
                                Save
                            </Button>
                        </Group>
                    )}
                </Group>
            </Stack>

            {errorMessage && isEditing && (
                <Alert color="red" title="Could not save worker type">
                    {errorMessage}
                </Alert>
            )}

            {pageState === 'reading' ? (
                <>
                    <Paper withBorder p="md" radius="md">
                        <Stack gap="sm">
                            <div>
                                <Text size="sm" c="dimmed">
                                    Name
                                </Text>
                                <Text>{workerType.name}</Text>
                            </div>
                            <div>
                                <Text size="sm" c="dimmed">
                                    Role
                                </Text>
                                <Text>{formatWorkerTypeRoleLabel(workerType)}</Text>
                            </div>
                        </Stack>
                    </Paper>

                    <Paper withBorder p="md" radius="md">
                        <Stack gap="sm">
                            <Title order={4}>Workers</Title>

                            {workersLoading ? (
                                <Text size="sm" c="dimmed">
                                    Loading...
                                </Text>
                            ) : workersErrorMessage ? (
                                <Stack gap="sm">
                                    <Alert color="red" title="Could not load workers">
                                        {workersErrorMessage}
                                    </Alert>
                                    <Button
                                        onClick={() => void refetchWorkers()}
                                        variant="light"
                                        size="compact-sm"
                                    >
                                        Retry
                                    </Button>
                                </Stack>
                            ) : workers.length === 0 ? (
                                <Text size="sm" c="dimmed">
                                    No workers of this type yet.
                                </Text>
                            ) : (
                                <Stack gap="xs">
                                    {workers.map((worker: Worker) => (
                                        <Text
                                            key={worker.id}
                                            component={Link}
                                            to="/workers/$workerId"
                                            params={{workerId: worker.id}}
                                            size="sm"
                                        >
                                            {worker.name}
                                        </Text>
                                    ))}
                                </Stack>
                            )}
                        </Stack>
                    </Paper>
                </>
            ) : (
                <Paper withBorder p="md" radius="md">
                    <Stack gap="md">
                        <TextInput label="Name" withAsterisk {...form.getInputProps('name')} />
                        <WorkerTypeRoleSwitches
                            isDriver={form.getInputProps('isDriver', {type: 'checkbox'})}
                            isTechnician={form.getInputProps('isTechnician', {type: 'checkbox'})}
                        />
                    </Stack>
                </Paper>
            )}
        </Stack>
    )
}
